import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Truck,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { allocatedToOTMt } from "@/features/contracts/lib/contract-progress"
import { kgToMt, formatNumber, percent } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { Contract } from "@/types/domain"

interface ContractSummaryCardsProps {
  contract: Contract
}

interface SummaryCardProps {
  label: string
  value: string
  hint?: string
  icon: React.ComponentType<{ className?: string }>
  tone: "info" | "warning" | "success" | "danger" | "muted"
}

const toneStyles: Record<SummaryCardProps["tone"], { bg: string; text: string; iconBg: string; iconText: string }> = {
  info: {
    bg: "",
    text: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-100 dark:bg-sky-500/15",
    iconText: "text-sky-600 dark:text-sky-300",
  },
  warning: {
    bg: "",
    text: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconText: "text-amber-600 dark:text-amber-300",
  },
  success: {
    bg: "",
    text: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
    iconText: "text-emerald-600 dark:text-emerald-300",
  },
  danger: {
    bg: "",
    text: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-100 dark:bg-red-500/15",
    iconText: "text-red-600 dark:text-red-300",
  },
  muted: {
    bg: "",
    text: "text-foreground",
    iconBg: "bg-muted",
    iconText: "text-muted-foreground",
  },
}

function SummaryCard({ label, value, hint, icon: Icon, tone }: SummaryCardProps) {
  const style = toneStyles[tone]
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">{label}</p>
          <p className={cn("text-2xl font-semibold", style.text)}>{value}</p>
          {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
        </div>
        <div className={cn("rounded-lg p-2", style.iconBg)}>
          <Icon className={cn("size-5", style.iconText)} />
        </div>
      </CardContent>
    </Card>
  )
}

export function ContractSummaryCards({ contract }: ContractSummaryCardsProps) {
  const { t } = useTranslation(["contracts", "common"])

  const totalQty = contract.quantity ?? 0
  const orders = contract.Orders ?? []

  const allocatedMt = orders.reduce(
    (sum, o) => sum + kgToMt(o.agreedQuantity ?? 0),
    0,
  )
  const allocatedPercent = percent(allocatedMt, totalQty)

  const shippedMt = allocatedToOTMt(contract)
  const remainingMt = Math.max(totalQty - shippedMt, 0)
  const remainingPct = percent(remainingMt, totalQty)

  const totalProducers = contract.totalProducers ?? 0
  const traceableProducers = contract.traceableProducers ?? 0
  const traceabilityPercent = percent(traceableProducers, totalProducers)

  const traceabilityTone: SummaryCardProps["tone"] =
    totalProducers === 0
      ? "warning"
      : traceabilityPercent === 100
        ? "success"
        : "danger"
  const TraceabilityIcon =
    totalProducers === 0
      ? Clock
      : traceabilityPercent === 100
        ? CheckCircle2
        : ShieldAlert

  const traceabilityValue =
    totalProducers === 0
      ? t("summary.inProgress")
      : `${formatNumber(traceabilityPercent)} %`
  const traceabilityHint =
    totalProducers === 0
      ? t("summary.noProducers")
      : t("summary.producersTraceable", {
          traceable: traceableProducers,
          total: totalProducers,
        })

  // EUDR placeholder until deliveries land
  const eudrTone: SummaryCardProps["tone"] = totalProducers === 0 ? "warning" : "muted"
  const EudrIcon = totalProducers === 0 ? Clock : AlertCircle
  const eudrValue =
    totalProducers === 0 ? t("summary.inProgress") : t("summary.noDataAssigned")

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        label={t("summary.totalQuantity")}
        value={`${formatNumber(totalQty)} ${t("units.mt", { ns: "common" })}`}
        hint={t("summary.allocated", { percent: formatNumber(allocatedPercent) })}
        icon={BarChart3}
        tone="info"
      />
      <SummaryCard
        label={t("summary.remaining")}
        value={`${formatNumber(remainingMt)} ${t("units.mt", { ns: "common" })}`}
        hint={t("summary.remainingPercent", { percent: formatNumber(remainingPct) })}
        icon={Truck}
        tone="warning"
      />
      <SummaryCard
        label={t("summary.traceabilityLevel")}
        value={traceabilityValue}
        hint={traceabilityHint}
        icon={TraceabilityIcon}
        tone={traceabilityTone}
      />
      <SummaryCard
        label={t("summary.eudrCompliance")}
        value={eudrValue}
        icon={EudrIcon}
        tone={eudrTone}
      />
    </div>
  )
}
